import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { Media } from '@/components/Media'
import { AKTUELL_CATEGORY_LABEL } from '@/components/Aktuell/categories'
import type { AktuellListItem } from '@/utilities/getCachedAktuell'

type Props = {
  item: AktuellListItem
}

const formatDate = (iso?: string | null) => {
  if (!iso) return null
  try {
    return new Intl.DateTimeFormat('de-CH', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(new Date(iso))
  } catch {
    return null
  }
}

export function AktuellCard({ item }: Props) {
  const href = `/aktuell/${item.slug}`
  const categoryLabel = AKTUELL_CATEGORY_LABEL[item.category] ?? item.category
  const dateLabel = formatDate(item.publishedAt)

  return (
    <Link
      href={href}
      className="group flex flex-col gap-6 w-full"
      aria-label={`Beitrag öffnen: ${item.title}`}
    >
      <div className="relative h-[300px] w-full overflow-hidden">
        {typeof item.featuredImage === 'object' && item.featuredImage ? (
          <div className="absolute inset-0 transition-transform duration-700 ease-out group-hover:scale-[1.04]">
            <Media
              resource={item.featuredImage}
              alt={item.title}
              fill
              pictureClassName="block h-full w-full"
              imgClassName="h-full w-full object-cover"
              size="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
            />
          </div>
        ) : (
          <div className="absolute inset-0 bg-brand-surface-2" />
        )}

        {/* Tag flush bottom-left */}
        <span className="absolute bottom-0 left-0 z-10 inline-flex items-center font-metropolis uppercase bg-brand text-text-white px-3 py-1 text-base">
          {categoryLabel}
        </span>

        {/* Arrow square flush bottom-right — same pattern as DetailedProjectEntry */}
        <div className="absolute bottom-0 right-0 w-12 h-12 bg-brand flex items-center justify-center z-10">
          <ArrowUpRight className="w-6 h-6 text-white transition-transform duration-200 rotate-45 group-hover:rotate-90" />
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3">
          {dateLabel && item.publishedAt && (
            <time
              className="label-md text-secondary"
              dateTime={new Date(item.publishedAt).toISOString()}
            >
              {dateLabel}
            </time>
          )}
          <h3 className="heading-xs text-primary text-balance transition-colors duration-300 group-hover:text-brand">
            {item.title}
          </h3>
          <p className="body-base text-secondary line-clamp-3 leading-snug">
            {item.shortDescription}
          </p>
        </div>
        <div className="pt-1">
          <span className="inline-flex items-center gap-2 font-ui font-medium text-base text-text-brand group-hover:text-brand/80 transition-colors duration-300">
            <span className="uppercase">Zum Beitrag</span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              className="transition-transform duration-300 group-hover:translate-x-1"
            >
              <path
                d="M9 18L15 12L9 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
      </div>
    </Link>
  )
}
